/* eslint-disable import/no-cycle */
import {action, decorate, observable} from 'mobx';
import {get} from 'lodash';
import i18n from '../i18n/i18n';
import {Api} from '../middleware/api';
import {EntityStore, LoadStrategies} from './entityStore';
import {Consumption} from '../models/consumption';
import {
  consumeIngredientEndpoint, consumeRetrogradeEndpoint, validateConsumptionEndpoint
} from '../middleware/endpoints/consumptions';

export class ConsumptionStore extends EntityStore {
  consumptions = [];
  validationError = null;
  validationWarning = null;

  constructor(rootStore) {
    super(rootStore, 'consumptions', Api.consumptions, Consumption);
  }

  getDependencies() {
    return [
      {
        store: this.rootStore.batchStore,
        modelId: 'batchId',
      },
      {
        store: this.rootStore.componentStore,
        modelId: 'componentId',
      },
    ];
  }

  filterBy({preparationId, componentId}) {
    let result = this.consumptions;
    if (preparationId) {
      result = result.filter((consumption) => consumption.preparationId === preparationId);
    }
    if (componentId) {
      result = result.filter((consumption) => get(consumption, 'componentId') === componentId);
    }
    return result;
  }

  loadByTargetYieldIds(targetYieldIds) {
    return this.loadAllWithDependencies({
      strategy: LoadStrategies.replace,
      params: {
        targetYieldIds,
      },
    });
  }

  actualConsumed(preparationId, componentId) {
    const consumptions = this.filterBy({preparationId, componentId});
    return consumptions.reduce((acc, curr) => acc + curr.amount, 0);
  }

  async consumeIngredient({preparationId, ingredientNo, amount, position}) {
    const query = this.api[consumeIngredientEndpoint]({preparationId, ingredientNo, amount, position});
    query.then((response) => {
      const {data} = response;
      this.rootStore.consumptionStore.loadAllWithDependencies(
        {params: {preparationId, componentId: data.componentId}}
      );
      this.materialConsumedSuccess(data.componentId, data.amount);
    });
    return query.catch((err) => {
      if (err.name === 'E_BAD_REQUEST') {
        this.rootStore.flashMessageStore.addFlashMessage({
          type: 'error',
          title: i18n.t(`batchHandling.consumption.errors.${err.error?.messages[0]}`),
        });
      }
    });
  }

  consumeRetrograde({preparationId, componentId}) {
    return this.api[consumeRetrogradeEndpoint]({preparationId, componentId})
      .then((response) => {
        const {data} = response;
        this.rootStore.consumptionStore.loadAll(
          {params: {preparationId, componentId: data.componentId}}
        );
        this.materialConsumedSuccess(data.componentId, data.amount);
      }).catch((err) => {
        this.handleApiError(err);
      });
  }

  materialConsumedSuccess(componentId, amount) {
    const component = this.rootStore.componentStore.getById(componentId);
    this.rootStore.flashMessageStore.addFlashMessage({
      type: 'success',
      title: i18n.t('batchHandling.preparation.materialConsumed',
        {
          materialName: component.material?.name,
          amount,
          unitOfMeasureLabel: component.unitOfMeasure.label,
        }),
    });
  }

  /**
   * validates whether a ingredient can be consumed
   *
   * Validation errors:
   * t('batchHandling.consumption.errors.E_INTERNAL_SERVER_ERROR');
   * t('batchHandling.consumption.errors.E_INGREDIENT_NOT_FOUND');
   * t('batchHandling.consumption.errors.E_ALREADY_CONSUMED');
   * t('batchHandling.consumption.errors.E_BATCH_LOCKED');
   * t('batchHandling.consumption.errors.E_BATCH_EXPIRED');
   * t('batchHandling.consumption.errors.E_RESERVED');
   * t('batchHandling.consumption.errors.E_MATERIAL_NOT_RELEVANT');
   * t('batchHandling.consumption.errors.E_IS_RETROGRADE');
   * t('batchHandling.consumption.errors.E_AMOUNT_INVALID');
   * t('batchHandling.consumption.errors.E_STOCK_TOO_LOW');
   * t('batchHandling.consumption.errors.E_INVALID_BARCODE');
   * t('batchHandling.consumption.errors.E_COULD_NOT_VALIDATE');
   * t('batchHandling.consumption.errors.W_POSITION_INCORRECT');
   *
   * Validation warnings:
   * t('batchHandling.consumption.warnings.W_TOLERANCE_HIGH_EXCEEDED');
   *
   * @param preparationId
   * @param ingredientNo
   * @param amount
   * @param position
   */
  async validate({preparationId, ingredientNo, amount, position}) {
    return this.api[validateConsumptionEndpoint]({preparationId, ingredientNo, amount, position});
  }

  reloadConsumptions(params) {
    this.loadAllWithDependencies({
      params,
      dependencies: [
        {
          store: this.rootStore.batchStore,
          modelId: 'batchId',
        },
      ],
    }).then(() => {
      this.consumptions = this.consumptions.slice();
    });
  }

  getByComponentId(componentId) {
    return this.consumptions.filter((c) => c.componentId === componentId);
  }

  getByTargetYieldIds(targetYieldIds) {
    return this.consumptions.filter((c) => targetYieldIds.find((ty) => ty === c.targetYieldId));
  }
}

decorate(ConsumptionStore, {
  consumptions: observable,
  validate: action,
  consume: action,
});
